import { aaConfig } from '@ad-lucy/aa/config';
import { bdConfig } from '@ad-lucy/bd/config';
import { bpConfig } from '@ad-lucy/bp/config';
import { ceaConfig } from '@ad-lucy/ceaxl/config';
import {
  CREATIVE_TYPE,
  type CreativesConfig,
  defaultCreativesConfig,
  PRODUCT_TYPE,
} from '@ad-lucy/config/creatives';
import type { GlobalConfig, KnownProductTypes } from '@ad-lucy/context/adData';
import { LoggerService } from '@ad-lucy/hooks/LoggerService';
import { raConfig } from '@ad-lucy/ra/config';
import { saConfig } from '@ad-lucy/sa/config';
import { stConfig } from '@ad-lucy/st/config';
import { vvConfig } from '@ad-lucy/vv/config';
import { type FC, type LazyExoticComponent, lazy, useEffect } from 'react';

type ProductTypeMap = Record<
  KnownProductTypes,
  {
    config: CreativesConfig<never>;
    content: LazyExoticComponent<FC>;
  }
>;

const AAContent = lazy(() => import('@ad-lucy/aa/containers/Content'));
const BPContent = lazy(() => import('@ad-lucy/bp/containers/Content'));
const BDContent = lazy(() => import('@ad-lucy/bd/containers/Content'));
const CEAXLContent = lazy(() => import('@ad-lucy/ceaxl/containers/Content'));
const RAContent = lazy(() => import('@ad-lucy/ra/containers/Content'));
const SAContent = lazy(() => import('@ad-lucy/sa/containers/Content'));
const STContent = lazy(() => import('@ad-lucy/st/containers/Content'));
const VVContent = lazy(() => import('@ad-lucy/vv/containers/Content'));

export const productTypeMap = {
  [PRODUCT_TYPE.AA_2]: {
    config: aaConfig,
    content: AAContent,
  },
  [PRODUCT_TYPE.BP_2]: {
    config: bpConfig,
    content: BPContent,
  },
  [PRODUCT_TYPE.BD]: {
    config: bdConfig,
    content: BDContent,
  },
  [PRODUCT_TYPE.CEAXL_2]: {
    config: ceaConfig,
    content: CEAXLContent,
  },
  [PRODUCT_TYPE.RA]: {
    config: raConfig,
    content: RAContent,
  },
  [PRODUCT_TYPE.SA_2]: {
    config: saConfig,
    content: SAContent,
  },
  [PRODUCT_TYPE.ST]: {
    config: stConfig,
    content: STContent,
  },
  [PRODUCT_TYPE.VV]: {
    config: vvConfig,
    content: VVContent,
  },
} as const satisfies ProductTypeMap;

const logger = LoggerService.getLogger().getModuleLogger('useCreative');

const getProductType = (globalConfig: GlobalConfig | undefined): KnownProductTypes | undefined => {
  const version = globalConfig?.version;
  if (!version) return undefined;

  if (version.productType) {
    return version.productType;
  }

  // In the "Live Preview" mode, the productType is not available,
  // so we need to extract it from the Creative field (pattern: "{productType}@{version}")
  if (version.Creative) {
    const [extractedProductType = ''] = version.Creative.split('@');
    const productType = extractedProductType.toLowerCase();

    // Map single app names to their _2 variants for compatibility
    if (productType === CREATIVE_TYPE.AA) return PRODUCT_TYPE.AA_2;
    if (productType === CREATIVE_TYPE.BP) return PRODUCT_TYPE.BP_2;
    if (productType === CREATIVE_TYPE.SA) return PRODUCT_TYPE.SA_2;
    if (productType === CREATIVE_TYPE.CEAXL) return PRODUCT_TYPE.CEAXL_2;

    return productType as KnownProductTypes;
  }

  return undefined;
};

export const useCreative = (globalConfig: GlobalConfig | undefined) => {
  const productType = getProductType(globalConfig);

  useEffect(() => {
    if (productType && !(productType in productTypeMap)) {
      logger.error('Unknown product type: ', productType);
    }
  }, [productType]);

  if (productType) {
    return productTypeMap[productType];
  }
  return { config: defaultCreativesConfig, content: () => null };
};
